import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { CSSProperties, Ref } from 'react';
import { useEffect, useRef, useState } from 'react';

import { EYEBROW, NAMESPACE, PauseChip, Picture, Rich } from '../components/ui';
import { initHeroSim, type SimHandle } from '../engine/hero-sim';
import { numberFormatter } from '../strings';

// The design's first paint of the chart (the settled state): the engine redraws it from its own state
// as soon as it boots, so these only matter for the frame before that.
const BAD_PATH = 'M0.0 48 15.4 41.8 30.8 38.5 46.2 42.8 61.5 39.5 76.9 48.5 92.3 38 107.7 28.5 123.1 42.3 138.5 48.5 153.8 45.7 169.2 21.3 184.6 39 200.0 18 215.4 39.9 230.8 42.3 246.2 43.3 261.5 38.5 276.9 44.7 292.3 37.6 307.7 21.3 323.1 37.1 338.5 44.2 353.8 36.6 369.2 35.6 384.6 47.1 400.0 38.5 415.4 43.3 430.8 37.1 446.2 38.5 461.5 40.4 476.9 38.5 492.3 45.7 507.7 41.8 523.1 40.9 538.5 43.3 553.8 48.5 569.2 42.8 584.6 47.6 600.0 45.2';
const GOOD_PATH = 'M0 140.1 15.4 140.1 30.8 140.1 46.2 140.6 61.5 140.6 76.9 140.6 92.3 140.1 107.7 139.6 123.1 140.6 138.5 140.1 153.8 139.6 169.2 140.6 184.6 139.6 200 140.1 215.4 140.6 230.8 140.6 246.2 140.1 261.5 140.1 276.9 140.6 292.3 140.1 307.7 140.6 323.1 139.6 338.5 140.1 353.8 140.6 369.2 140.1 384.6 139.6 400 140.6 415.4 140.1 430.8 140.6 446.2 140.6 461.5 139.6 476.9 140.6 492.3 139.6 507.7 139.6 523.1 140.1 538.5 140.6 553.8 140.1 569.2 140.6 584.6 139.6 600 140.1';
const AREA_TAIL = ' L600 160 L0 160 Z';

const PROS = [
  { name: 'pro-coldzera', alt: 'coldzera' },
  { name: 'pro-sacy', alt: 'Sacy' },
  { name: 'pro-brtt', alt: 'brTT' },
  { name: 'pro-gaules', alt: 'Gaules' },
];

const POINTS = [
  {
    key: 'tools',
    icon: (
      <>
        <rect x='3.5' y='3.5' width='7' height='7' rx='2' />
        <rect x='13.5' y='3.5' width='7' height='7' rx='2' />
        <rect x='3.5' y='13.5' width='7' height='7' rx='2' />
        <path d='M17 13.5v7M13.5 17h7' />
      </>
    ),
  },
  {
    key: 'routes',
    icon: (
      <>
        <path d='M4 12h3.5' />
        <path d='M16.5 12H20' />
        <path d='M7.5 12c2 0 2.5-5 4.5-5s2.5 10 4.5 10' />
        <circle cx='12' cy='12' r='9.2' />
      </>
    ),
  },
  {
    key: 'servers',
    icon: (
      <>
        <circle cx='12' cy='12' r='8.5' />
        <path d='M3.5 12h17M12 3.5c2.2 2.4 3.3 5.3 3.3 8.5S14.2 18.1 12 20.5c-2.2-2.4-3.3-5.3-3.3-8.5S9.8 5.9 12 3.5Z' />
      </>
    ),
  },
  {
    key: 'players',
    icon: (
      <>
        <path d='M12 3.6 5 6.3v5.4c0 4.3 2.9 7.5 7 8.7 4.1-1.2 7-4.4 7-8.7V6.3l-7-2.7Z' />
        <path d='m8.9 12.2 2.1 2.1 4.1-4.4' />
      </>
    ),
  },
];

const Arrow = ({ className, size }: { className: string; size: number }) => (
  <svg className={className} width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
    <path d={size === 14 ? 'M5 12h13' : 'M4 12h14'} />
    <path d='m13 7 5 5-5 5' />
  </svg>
);

const bar = (width: string) => ({ '--w': width }) as CSSProperties;

function HeroSim() {
  const { t } = useTranslation(NAMESPACE);
  const { locale = 'pt' } = useRouter();
  const cardRef = useRef<HTMLElement>(null);
  const simRef = useRef<SimHandle | null>(null);
  const [paused, setPaused] = useState(false);
  const decimal = numberFormatter(locale, 1);

  useEffect(() => {
    if (!cardRef.current) return undefined;
    const sim = initHeroSim(cardRef.current, { paused, decimal: numberFormatter(locale, 1) });
    simRef.current = sim;
    return () => {
      sim.destroy();
      simRef.current = null;
    };
    // the engine owns its own clock; the pause state is handed over below, not by re-creating it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    simRef.current?.setPaused(paused);
  }, [paused]);

  // pre-set to paused under prefers-reduced-motion, so the chip already says what it will do
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setPaused(true);
  }, []);

  return (
    <figure ref={cardRef} className='switch mt-[clamp(1.75rem,1.2rem+2vw,2.5rem)] rounded-3xl border border-white/10 bg-[radial-gradient(120%_100%_at_0%_0%,rgb(147_255_24/.07),transparent_58%),linear-gradient(180deg,rgb(255_255_255/.055),rgb(255_255_255/.015))] p-[clamp(.9rem,.6rem+1vw,1.15rem)] shadow-[0_24px_60px_rgb(0_0_0/.55),0_8px_20px_rgb(0_0_0/.35),inset_0_1px_0_rgb(255_255_255/.07)] backdrop-blur-[10px] forced-colors:border-[CanvasText] forced-colors:bg-[Canvas] [@media(width>=72.5rem)]:mt-[clamp(1rem,2.8vh,2rem)] [@media(width>=72.5rem)]:p-[clamp(.75rem,1.6vh,1rem)]' data-hsim>
      <figcaption className='switch__bar'>
        <span className='switch__k'>
          <i aria-hidden='true' />
          <span className='switch__kt'>{t('hero.sim.caption')}</span>
        </span>
        <span className='switch__tools'>
          <PauseChip paused={paused} onToggle={() => setPaused((previous) => !previous)} pauseAria={t('hero.sim.pause_aria')} resumeAria={t('hero.sim.resume_aria')} pauseLabel={t('common.pause')} resumeLabel={t('common.resume')} />
        </span>
      </figcaption>

      <div className='switch__chart'>
        <svg className='switch__svg' viewBox='0 0 600 160' preserveAspectRatio='none' width='600' height='160' aria-hidden='true' focusable='false'>
          <defs>
            <linearGradient id='sw-bad' x1='0' x2='0' y1='0' y2='1'>
              <stop offset='0' stopColor='#FF5C6C' stopOpacity='.36' />
              <stop offset='1' stopColor='#FF5C6C' stopOpacity='0' />
            </linearGradient>
            <linearGradient id='sw-good' x1='0' x2='0' y1='0' y2='1'>
              <stop offset='0' stopColor='#93FF18' stopOpacity='.42' />
              <stop offset='1' stopColor='#93FF18' stopOpacity='0' />
            </linearGradient>
          </defs>
          <g className='switch__rules'>
            <path d='M0 44H600' />
            <path d='M0 83H600' />
            <path d='M0 122H600' />
          </g>
          <path className='switch__area switch__area--bad' data-hsim-badarea d={BAD_PATH + AREA_TAIL} />
          <path className='switch__line switch__line--bad' data-hsim-badline d={BAD_PATH} />
          <path className='switch__area switch__area--good' data-hsim-goodarea d={GOOD_PATH + AREA_TAIL} />
          <path className='switch__line switch__line--good' data-hsim-goodline d={GOOD_PATH} />
        </svg>
        <span className='switch__tag switch__tag--bad'>{t('hero.sim.without')}</span>
        <span className='switch__tag switch__tag--good'>{t('hero.sim.with')}</span>
      </div>

      <p className='sr-only'>{t('hero.sim.sr')}</p>

      <ul className='switch__rows' aria-hidden='true'>
        <li className='swrow'>
          <span className='swrow__k'>{t('hero.sim.ping')}</span>
          <span className='swrow__bad tabular'>
            <b data-hsim-off>208</b>
            <em>ms</em>
          </span>
          <Arrow className='swrow__arrow' size={18} />
          <span className='swrow__good tabular'>
            <b data-hsim-on>4</b>
            <em>ms</em>
          </span>
          <span className='swrow__chip' data-hsim-chip>
            −98%
          </span>
          <span className='swrow__bars'>
            <i className='swrow__bar swrow__bar--bad' style={bar('86.7%')} data-hsim-bar='ping-bad' />
            <i className='swrow__bar swrow__bar--good' style={bar('3.5%')} data-hsim-bar='ping-good' />
          </span>
        </li>
        <li className='swrow'>
          <span className='swrow__k'>{t('hero.sim.loss')}</span>
          <span className='swrow__bad tabular'>
            <b data-hsim-loss>{decimal(5.5)}</b>
            <em>%</em>
          </span>
          <Arrow className='swrow__arrow' size={18} />
          <span className='swrow__good tabular'>
            <b>{decimal(0)}</b>
            <em>%</em>
          </span>
          <span className='swrow__chip swrow__chip--flat'>{t('hero.sim.zeroed')}</span>
          <span className='swrow__bars'>
            <i className='swrow__bar swrow__bar--bad' style={bar('68.8%')} data-hsim-bar='loss-bad' />
            <i className='swrow__bar swrow__bar--good' style={bar('3.5%')} data-hsim-bar='loss-good' />
          </span>
        </li>
        <li className='swrow'>
          <span className='swrow__k'>{t('hero.sim.jitter')}</span>
          <span className='swrow__bad tabular'>
            <b data-hsim-jit>30</b>
            <em>ms</em>
          </span>
          <Arrow className='swrow__arrow' size={18} />
          <span className='swrow__good tabular'>
            <b>2</b>
            <em>ms</em>
          </span>
          <span className='swrow__chip swrow__chip--flat'>{t('hero.sim.stable')}</span>
          <span className='swrow__bars'>
            <i className='swrow__bar swrow__bar--bad' style={bar('75%')} data-hsim-bar='jit-bad' />
            <i className='swrow__bar swrow__bar--good' style={bar('5%')} data-hsim-bar='jit-good' />
          </span>
        </li>
      </ul>

      <p className='switch__foot'>
        <span className='switch__hop'>
          <span className='switch__hop__a'>{t('hero.sim.from')}</span>
          <Arrow className='switch__arw' size={14} />
          <span className='switch__hop__b'>{t('hero.sim.to')}</span>
        </span>
        <span className='switch__q'>
          <Rich i18nKey='hero.sim.route' />
        </span>
        <span className='switch__note'>{t('hero.sim.note')}</span>
      </p>
    </figure>
  );
}

interface HeroProps {
  /** the page moves the signup form's node into this slot (and into the trial dialog while it is open) */
  formSlotRef: Ref<HTMLDivElement>;
}

export function Hero({ formSlotRef }: HeroProps) {
  const { t } = useTranslation(NAMESPACE);

  return (
    <section className='relative isolate overflow-clip pb-[clamp(3rem,2rem+4vw,5.5rem)] pt-[clamp(2rem,1rem+4vw,4.5rem)] [@media(max-height:30rem)_and_(min-width:40em)]:pb-10 [@media(max-height:30rem)_and_(min-width:40em)]:pt-6 [@media(width>=72.5rem)]:flex [@media(width>=72.5rem)]:min-h-[calc(100vh-var(--header-h))] [@media(width>=72.5rem)]:flex-col [@media(width>=72.5rem)]:justify-center [@media(width>=72.5rem)]:pb-[clamp(1.25rem,4vh-.25rem,3.5rem)] [@media(width>=72.5rem)]:pt-[clamp(1.25rem,4vh-.5rem,3.5rem)]' id='topo' aria-labelledby='hero-title'>
      <div className='absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='hero__grid' />
        <span className='hero__map' />
        <span className='hero__halo hero__halo--a' />
        <span className='hero__halo hero__halo--b' />
        <span className='hero__beam' />
        <span className='hero__veil' />
      </div>

      <div className='mx-auto grid w-full max-w-[90rem] items-start gap-[clamp(2rem,1.2rem+3vw,3.5rem)] px-[min(var(--gutter),7vw)] [@media(width>=72.5rem)]:w-full [@media(width>=72.5rem)]:grid-cols-[minmax(0,1fr)_minmax(27rem,30rem)] [@media(width>=72.5rem)]:gap-[clamp(2.5rem,1rem+3vw,4.5rem)]'>
        <div className='min-w-0 [@media(width<72.5rem)]:mx-auto [@media(width<72.5rem)]:max-w-[46rem]'>
          <p className={`${EYEBROW} mb-4 mt-0 leading-[1.35] [@media(width>=72.5rem)]:mb-[clamp(.75rem,1.8vh,1.25rem)]`}>{t('hero.eyebrow')}</p>
          <h1 className='max-w-[15em] text-balance break-words text-[clamp(2.125rem,1.35rem+3.1vw,3.75rem)] font-bold leading-[1.02] tracking-[-.038em] text-white [&_em]:not-italic [&_em]:text-[#93FF18] [&_em]:[text-shadow:0_0_34px_rgb(147_255_24/.35)] [@media(width<22rem)]:[overflow-wrap:anywhere] [@media(width>=72.5rem)]:max-w-[15.5em] [@media(width>=72.5rem)]:text-[min(clamp(2.5rem,1rem+2.5vw,3.5rem),calc(2.1rem+1.7vh))]' id='hero-title'>
            <Rich i18nKey='hero.title' />
          </h1>
          <p className='mt-5 max-w-[34em] text-[length:clamp(1.0625rem,1rem+.3vw,1.1875rem)] leading-[1.55] text-[#CBD5E1] [&_b]:font-semibold [&_b]:text-white [@media(width>=72.5rem)]:mt-[clamp(.75rem,2vh,1.25rem)] [@media(width>=72.5rem)]:max-w-[36em]'>
            <Rich i18nKey='hero.lead' />
          </p>

          <ul className='mb-0 mt-6 grid list-none gap-3 p-0 [&>li]:flex [&>li]:min-w-0 [&>li]:items-center [&>li]:gap-[.7rem] [&>li]:text-[.875rem] [&>li]:leading-[1.35] [&>li]:text-[#9AA3B5] [&_b]:font-semibold [&_b]:text-white [&_svg]:box-border [&_svg]:size-8 [&_svg]:flex-none [&_svg]:rounded-[.55rem] [&_svg]:border [&_svg]:border-[#93FF18]/[.26] [&_svg]:bg-[#93FF18]/[.09] [&_svg]:p-1.5 [&_svg]:text-[#93FF18] [@media(width<19.5rem)]:[&>li]:items-start [@media(width<19.5rem)]:[&>li]:gap-2 [@media(width<19.5rem)]:[&_svg]:size-6 [@media(width<19.5rem)]:[&_svg]:p-1 [@media(width>=40rem)]:grid-cols-[1fr_1fr] [@media(width>=40rem)]:gap-x-5 [@media(width>=72.5rem)]:mt-[clamp(1rem,2.6vh,1.75rem)] [@media(width>=72.5rem)]:gap-y-[clamp(.5rem,1.3vh,.875rem)]'>
            {POINTS.map((point) => (
              <li key={point.key}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                  {point.icon}
                </svg>
                <span>
                  <Rich i18nKey={`hero.points.${point.key}`} />
                </span>
              </li>
            ))}
          </ul>

          <HeroSim />
        </div>

        <div className='hero__form min-w-0 [@media(width<72.5rem)]:mx-auto [@media(width<72.5rem)]:w-full [@media(width<72.5rem)]:max-w-[40rem]'>
          <p className={`${EYEBROW} mb-3 mt-0 leading-[1.35] [@media(width>=72.5rem)]:mb-[clamp(.5rem,1.4vh,.75rem)]`}>{t('hero.form_eyebrow')}</p>
          <div ref={formSlotRef} className='hero__form-slot' />
          <div className='mt-4 flex flex-wrap items-center gap-3 [@media(width>=72.5rem)]:mt-[clamp(.625rem,1.6vh,1rem)]'>
            <ul className='m-0 flex flex-none list-none p-0 [&>li:first-child]:ms-0 [&>li]:-ms-[7px] [&>li]:size-[26px] [&>li]:overflow-hidden [&>li]:rounded-full [&>li]:border [&>li]:border-[#93FF18]/35 [&>li]:bg-[#1C1E24] [&>li]:shadow-[0_2px_6px_rgb(0_0_0/.45),0_1px_2px_rgb(0_0_0/.3)] [&_img]:block [&_img]:size-full [&_img]:object-cover [@media(width<19.5rem)]:[&>li]:size-6' aria-label={t('hero.pros_aria')}>
              {PROS.map((pro) => (
                <li key={pro.name}>
                  <Picture name={pro.name} width={128} height={128} alt={pro.alt} />
                </li>
              ))}
            </ul>
            <p className='min-w-0 flex-[1_1_12rem] text-[.75rem] leading-[1.4] text-[#9AA3B5] [&_b]:font-bold [&_b]:text-[#93FF18]'>
              <Rich i18nKey='hero.rating' />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
